/**
 * Montagem do JSON-LD (dados estruturados).
 *
 * Regra do projeto: o schema só pode afirmar o que está visível na página e
 * todos os valores vêm das tabelas `doctor` e `clinic`. Nada é escrito aqui.
 * Campo vazio no banco não vira propriedade vazia: a propriedade some.
 * Nunca gere aggregateRating, nota ou número de avaliações.
 */

import { absoluteUrl } from "@/lib/site";
import type { SiteData } from "@/lib/site-data.functions";

export type JsonLdObject = Record<string, unknown>;

type Doctor = NonNullable<SiteData["doctor"]>;
type Clinic = NonNullable<SiteData["clinic"]>;

function preenchido(valor: unknown): boolean {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === "string") return valor.trim() !== "";
  if (Array.isArray(valor)) return valor.length > 0;
  return true;
}

/** Remove propriedades vazias, nulas ou indefinidas do objeto. */
function limpo(objeto: JsonLdObject): JsonLdObject {
  return Object.fromEntries(Object.entries(objeto).filter(([, valor]) => preenchido(valor)));
}

/**
 * Physician, a partir da tabela doctor.
 *
 * O schema só pode afirmar o que está na tela. Por isso existem duas versões:
 * a reduzida (padrão) afirma apenas o que o rodapé mostra em todas as páginas
 * (nome, CRM e especialidades dos RQEs). A completa acrescenta formação
 * (alumniOf) e sociedades (memberOf), que só aparecem para o leitor na página
 * /sobre/ e por isso só podem ser usadas lá.
 */
export type PhysicianSchemaVersao = "reduzida" | "completa";

export function physicianSchema(
  doctor: Doctor | null,
  versao: PhysicianSchemaVersao = "reduzida",
): JsonLdObject | null {
  if (!doctor) return null;

  const especialidades = (doctor.rqes ?? [])
    .map((rqe) => rqe.especialidade)
    .filter((especialidade): especialidade is string => preenchido(especialidade));

  const sociedades = (doctor.societies ?? []).filter((sociedade) => preenchido(sociedade));

  const completa = versao === "completa";

  return limpo({
    "@context": "https://schema.org",
    "@type": "Physician",
    name: doctor.name,
    url: absoluteUrl("/sobre/"),
    identifier: preenchido(doctor.crm)
      ? { "@type": "PropertyValue", name: "CRM", value: doctor.crm }
      : null,
    medicalSpecialty: especialidades,
    // Propriedades da versão completa: só onde formação e sociedades estão
    // visíveis em tela (hoje, apenas /sobre/).
    memberOf: completa
      ? sociedades.map((sociedade) => ({
          "@type": "MedicalOrganization",
          name: sociedade,
        }))
      : null,
    alumniOf: completa && preenchido(doctor.graduation)
      ? { "@type": "CollegeOrUniversity", name: doctor.graduation }
      : null,
    // sameAs: perfis oficiais do banco. Instagram e YouTube aparecem no rodapé
    // de todas as páginas, então valem nas duas versões.
    sameAs: (doctor.same_as ?? []).filter((url) => preenchido(url)),
  });
}

/** MedicalBusiness, a partir da tabela clinic. */
export function localBusinessSchema(clinic: Clinic | null): JsonLdObject | null {
  if (!clinic) return null;

  // O bairro entra junto da rua, para o endereço ficar idêntico ao do perfil
  // do Google do consultório.
  const rua = [clinic.street, clinic.district].filter(preenchido).join(", ");

  const endereco = limpo({
    "@type": "PostalAddress",
    streetAddress: rua,
    addressLocality: clinic.city,
    addressRegion: clinic.state,
    postalCode: clinic.postal_code,
    addressCountry: clinic.country,
  });


  const temGeo = preenchido(clinic.latitude) && preenchido(clinic.longitude);
  const horarios = Array.isArray(clinic.opening_hours) ? clinic.opening_hours : [];

  return limpo({
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: clinic.name,
    address: Object.keys(endereco).length > 1 ? endereco : null,
    telephone: clinic.phone,
    email: clinic.email,
    url: absoluteUrl("/"),
    hasMap: clinic.google_maps_url,
    geo: temGeo
      ? {
          "@type": "GeoCoordinates",
          latitude: clinic.latitude,
          longitude: clinic.longitude,
        }
      : null,
    openingHours: horarios,
  });
}

/** MedicalWebPage da página atual. */
export function medicalWebPageSchema({
  path,
  title,
  description,
  datePublished,
  dateModified,
}: {
  path: string;
  title?: string | null;
  description?: string | null;
  datePublished?: string | null;
  dateModified?: string | null;
}): JsonLdObject {
  return limpo({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    url: absoluteUrl(path),
    name: title,
    description,
    inLanguage: "pt-BR",
    datePublished,
    dateModified,
  });
}

/**
 * Nível da trilha. `path` é opcional de propósito: nível que ainda não tem
 * página no site é texto, não link, e por isso entra no schema sem `item`.
 * Breadcrumb apontando para 404 é estrutura quebrada e o Google segue o link.
 */
export type Trilha = { nome: string; path?: string | null };

/** BreadcrumbList, com posições começando em 1 e URLs absolutas. */
export function breadcrumbSchema(trilha: Trilha[]): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trilha.map((item, indice) =>
      limpo({
        "@type": "ListItem",
        position: indice + 1,
        name: item.nome,
        item: preenchido(item.path) ? absoluteUrl(item.path as string) : null,
      }),
    ),
  };
}

export type PerguntaFrequente = { pergunta: string; resposta: string };

/** FAQPage. Só use em página que mostra as perguntas na tela. */
export function faqSchema(perguntas: PerguntaFrequente[]): JsonLdObject | null {
  if (!perguntas.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: perguntas.map((item) => ({
      "@type": "Question",
      name: item.pergunta,
      acceptedAnswer: { "@type": "Answer", text: item.resposta },
    })),
  };
}
